// TODO: Rename to VertexDetailViewer - this component now shows vertex details
// (notes, metadata) even when there are no archives, and will include more
// vertex information in the future.

import { h } from 'preact';
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import type { Archive, Vertex, Preview } from '../types';
import * as api from '../services/api';
import { EditableNote } from './EditableNote';
import { copyWithFeedback } from '../../shared.js';
import { ShareIcon, CopyIcon, FullscreenIcon, ExitFullscreenIcon, ChevronLeftIcon, ChevronRightIcon } from './icons';
import { VertexLink } from './shared/VertexLink';
import { OutlinedButton } from './shared/OutlinedButton';

/**
 * Decode base64 string to UTF-8 text.
 * Unlike atob() which returns Latin-1, this properly decodes UTF-8 multi-byte sequences.
 */
function decodeBase64ToUtf8(base64: string): string {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

interface ArchiveViewerProps {
  vertexUrl: string;
  timestamp?: string;
  availableTimes?: string[];
  context?: 'coldstorage' | 'expiring';
  backendConfiguredLimit?: number;
  onBack: () => void;
  onNavigate: (timestamp: string) => void;
}

function contextBanner(context: 'coldstorage' | 'expiring' | undefined, archiveTime: string | null, limitSeconds: number | undefined): h.JSX.Element | null {
  if (!context || !archiveTime || !limitSeconds) return null;
  const archived = new Date(archiveTime).getTime();
  const deadline = archived + limitSeconds * 1000;
  const days = Math.max(0, Math.ceil((deadline - Date.now()) / (24 * 60 * 60 * 1000)));
  if (context === 'coldstorage') {
    return (
      <div class="archive-context-banner archive-context-coldstorage">
        This archive will move to cold storage in {days} day{days !== 1 ? 's' : ''}. Upgrade to Pro to keep it permanently.
      </div>
    );
  }
  return (
    <div class="archive-context-banner archive-context-expiry">
      This archive will be permanently deleted in {days} day{days !== 1 ? 's' : ''}. Upgrade to Pro to restore and keep it.
    </div>
  );
}

export function ArchiveViewer({
  vertexUrl,
  timestamp: initialTimestamp,
  availableTimes: initialAvailableTimes,
  context,
  backendConfiguredLimit,
  onBack,
  onNavigate,
}: ArchiveViewerProps) {
  const [archive, setArchive] = useState<Archive | null>(null);
  const [vertex, setVertex] = useState<Vertex | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const copyBtnRef = useRef<HTMLButtonElement>(null);

  // Internal state for times when not provided via props
  const [fetchedTimes, setFetchedTimes] = useState<string[] | null>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<string | null>(initialTimestamp || null);

  const availableTimes = initialAvailableTimes || fetchedTimes || [];
  const timestamp = currentTimestamp;

  // Load archive data
  useEffect(() => {
    loadData();
  }, [vertexUrl, initialTimestamp, initialAvailableTimes]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Fetch vertex and preview in parallel
      const [vertexData, previewData] = await Promise.all([
        api.fetchVertex(vertexUrl),
        api.fetchPreviewsBatch([vertexUrl]),
      ]);
      setVertex(vertexData);
      // Use first entry from response (key may differ from input due to normalisation)
      const previewEntry = Object.values(previewData)[0] || null;
      setPreview(previewEntry);

      // Fetch available times if not provided via props
      let times = initialAvailableTimes;
      let ts = initialTimestamp;

      if (!times) {
        const timesData = await api.fetchArchiveTimesBatch([vertexUrl]);
        const fetchedTimesArr = timesData[vertexUrl] || [];
        // Sort newest first
        const sortedTimes = [...fetchedTimesArr].sort((a, b) =>
          new Date(b).getTime() - new Date(a).getTime()
        );
        setFetchedTimes(sortedTimes);
        times = sortedTimes;

        // If no timestamp provided, use the latest
        if (!ts && sortedTimes.length > 0) {
          ts = sortedTimes[0];
          setCurrentTimestamp(ts);
        }
      }

      // If we have a timestamp, fetch the archive
      if (ts) {
        const archiveData = await api.fetchArchive(vertexUrl, ts);
        setArchive(archiveData);
        setShareId(archiveData.shareId || null);

        if (archiveData.shareId) {
          setShareUrl(`${window.location.origin}/archive/${archiveData.shareId}`);
        } else {
          setShareUrl(null);
        }
      }
    } catch (err) {
      console.error('Failed to load archive:', err);
      setError(err instanceof Error ? err.message : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }

  // Handle navigation - fetch new archive when timestamp changes
  async function handleNavigate(newTimestamp: string) {
    setCurrentTimestamp(newTimestamp);
    onNavigate(newTimestamp);

    try {
      setLoading(true);
      const archiveData = await api.fetchArchive(vertexUrl, newTimestamp);
      setArchive(archiveData);
      setShareId(archiveData.shareId || null);

      if (archiveData.shareId) {
        setShareUrl(`${window.location.origin}/archive/${archiveData.shareId}`);
      } else {
        setShareUrl(null);
      }
    } catch (err) {
      console.error('Failed to load archive:', err);
      setError(err instanceof Error ? err.message : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }

  async function handleShareArchive() {
    if (!archive) return;

    try {
      const id = await api.shareArchive(vertexUrl, timestamp);
      setShareId(id);
      setShareUrl(`${window.location.origin}/archive/${id}`);
    } catch (err) {
      alert(`Failed to share: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleCopyShareLink() {
    if (!shareUrl || !copyBtnRef.current) return;
    await copyWithFeedback(copyBtnRef.current, shareUrl);
  }

  async function handleSaveVertexNote(newNote: string | null) {
    if (!vertex) return;
    const response = await api.updateVertex(vertexUrl, newNote);
    setVertex({ ...vertex, note: newNote, updatedAt: response.updatedAt });
  }

  async function handleSaveArchiveNote(newNote: string | null) {
    if (!archive) return;
    await api.updateArchiveNote(vertexUrl, timestamp, newNote);
    setArchive({ ...archive, note: newNote });
  }

  function formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Sort timestamps newest first
  const sortedTimes = [...availableTimes].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  const currentIndex = timestamp ? sortedTimes.indexOf(timestamp) : -1;
  const hasPrev = currentIndex >= 0 && currentIndex < sortedTimes.length - 1;
  const hasNext = currentIndex > 0;

  // Memoize decoded HTML to avoid re-decoding on every render
  const decodedHtml = useMemo(() => {
    if (!archive?.archive) return '';
    const html = decodeBase64ToUtf8(archive.archive);
    // Inject style to disable all clicks within the archived page
    const disableClicksStyle = '<style>a,button,input,select,textarea,[onclick]{pointer-events:none!important;cursor:default!important}</style>';
    // Insert before </head> if present, otherwise prepend
    const headClose = html.indexOf('</head>');
    if (headClose !== -1) {
      return html.slice(0, headClose) + disableClicksStyle + html.slice(headClose);
    }
    return disableClicksStyle + html;
  }, [archive?.archive]);

  // The backend wraps binary PDFs as HTML with <embed src="data:application/pdf;base64,...">.
  // Chrome's PDF plugin won't activate inside a sandboxed srcDoc iframe, so extract the
  // bytes and serve them via a blob URL that the iframe loads as a top-level PDF.
  const pdfBlobUrl = useMemo(() => {
    if (archive?.sniffedInitialContentType !== 'PDF') return null;
    const m = decodedHtml.match(/data:application\/pdf;base64,([A-Za-z0-9+/=]+)/);
    if (!m) return null;
    const bytes = Uint8Array.from(atob(m[1]), c => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  }, [decodedHtml, archive?.sniffedInitialContentType]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const fullscreenButton = (
    <button
      class="archive-fullscreen-btn"
      onClick={toggleFullscreen}
      title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
    >
{isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
    </button>
  );

  if (loading) {
    return (
      <div class="archive-viewer">
        <div class="loading">Loading archive...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="archive-viewer">
        <div class="archive-viewer-header">
          <button onClick={onBack} class="btn-back">
            ← Back
          </button>
        </div>
        <div class="error-message">{error}</div>
      </div>
    );
  }

  // No archives available - show empty state
  if (!loading && availableTimes.length === 0 && vertex) {
    return (
      <div class="archive-viewer">
        <div class="archive-viewer-header">
          <button onClick={onBack} class="btn-back">
            ← Back
          </button>
          <h2>
            <VertexLink href={vertexUrl} class="archive-viewer-title-link">
              {preview?.title || vertexUrl}
            </VertexLink>
          </h2>
        </div>

        <EditableNote
          value={vertex.note}
          placeholder="Add a note for this vertex..."
          className="vertex-note archive-note"
          onSave={handleSaveVertexNote}
        />

        <div class="archive-empty-state">
          <p>No archives for this vertex.</p>
          <p class="archive-empty-subtitle">
            Archives are created when you annotate a page with the "Archive page" option enabled.
          </p>
          <VertexLink href={vertexUrl} class="archive-visit-link">
            Visit page →
          </VertexLink>
        </div>
      </div>
    );
  }

  if (!archive || !vertex) {
    return (
      <div class="archive-viewer">
        <div class="archive-viewer-header">
          <button onClick={onBack} class="btn-back">
            ← Back
          </button>
        </div>
        <div class="error-message">Archive not found</div>
      </div>
    );
  }

  return (
    <div class={`archive-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Fullscreen archive indicator bar + overlay */}
      {isFullscreen && (
        <>
          <div class="archive-fullscreen-bar">
            <span class="archive-fullscreen-label">Archived by Vertex</span>
            <span class="archive-fullscreen-date">{formatDate(timestamp!)}</span>
          </div>
          <div class="archive-fullscreen-overlay">
            <div class="archive-overlay-corner top-left" />
            <div class="archive-overlay-corner top-right" />
            <div class="archive-overlay-corner bottom-left" />
            <div class="archive-overlay-corner bottom-right" />
            <div class="archive-overlay-center" />
            <div class="archive-overlay-date">{formatDate(timestamp!)}</div>
          </div>
        </>
      )}
      {/* Header */}
      <div class="archive-viewer-header">
        <button onClick={onBack} class="btn-back">
          ← Back
        </button>
        <h2>
          <VertexLink href={vertexUrl} class="archive-viewer-title-link">
            {preview?.title || vertexUrl}
          </VertexLink>
        </h2>
      </div>

      {contextBanner(context, timestamp || null, backendConfiguredLimit)}

      {/* Vertex Note */}
      <EditableNote
        value={vertex.note}
        placeholder="Add a note for this vertex..."
        className="vertex-note archive-note"
        onSave={handleSaveVertexNote}
      />

      {/* Archive Navigation */}
      <div class="archive-viewer-meta">
        <button
          class="archive-nav-btn"
          disabled={!hasPrev}
          onClick={() => hasPrev && handleNavigate(sortedTimes[currentIndex + 1])}
          title="Previous archive"
        >
<ChevronLeftIcon />
        </button>
        <span>{formatDate(timestamp!)}</span>
        <button
          class="archive-nav-btn"
          disabled={!hasNext}
          onClick={() => hasNext && handleNavigate(sortedTimes[currentIndex - 1])}
          title="Next archive"
        >
<ChevronRightIcon />
        </button>
      </div>

      {/* Archive-specific Note */}
      <EditableNote
        value={archive.note}
        placeholder="Add a note for this archive..."
        className="vertex-note archive-note"
        onSave={handleSaveArchiveNote}
      />

      {/* Share Section */}
      <div class="archive-share-section">
        {shareId && shareUrl ? (
          <div class="shared-link-row">
            <button ref={copyBtnRef} onClick={handleCopyShareLink} class="btn-copy" title="Copy share link">
              <CopyIcon />
            </button>
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="share-link-anchor"
              title="Open shared archive"
            >
              {shareUrl}
            </a>
          </div>
        ) : (
          <OutlinedButton onClick={handleShareArchive}>
            <ShareIcon />
            Share
          </OutlinedButton>
        )}
      </div>

      {/* Archive iframe */}
      <div class="archive-frame-container">
        {fullscreenButton}
        {pdfBlobUrl ? (
          <iframe class="archive-frame" src={pdfBlobUrl} />
        ) : (
          <iframe
            class="archive-frame"
            sandbox="allow-same-origin"
            srcDoc={decodedHtml}
          />
        )}
      </div>
    </div>
  );
}
